<script setup>
  import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
  import { storeToRefs } from "pinia";
  import { useToastsStore } from "@/stores/toastsStore";

  import { Plus } from "lucide-vue-next";

  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import { Card } from "@/components/ui/card";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Label } from "@/components/ui/label";
  import { Button } from "@/components/ui/button";
  import { Select } from "@/components/episto-ui/select";

  import TemplateList from "./TemplateList.vue";
  import RecentRuns from "./RecentRuns.vue";
  import TemplateFormDialog from "./TemplateFormDialog.vue";

  import { handleTabKey } from "@composables/useTabIndent";
  import { useSurveyOptions } from "@utils/composables/filters/useSurveyOptions";
  import { useSqlQueryStore } from "@stores/sqlQuery";
  import { useCurrentUserStore } from "@stores/currentUser";
  import { useMCampaignsStore } from "@stores/mCampaigns";
  import { useAccountsStore } from "@stores/accounts";

  const toast = useToastsStore();

  const sqlQueryStore = useSqlQueryStore();
  const { recentRuns } = storeToRefs(sqlQueryStore);
  const { isSuperAdmin } = storeToRefs(useCurrentUserStore());

  const mCampaignsStore = useMCampaignsStore();
  const accountsStore = useAccountsStore();
  const { mCampaignsOptions, mCampaignsLoading } = storeToRefs(mCampaignsStore);
  const { availableAccountsOptions } = storeToRefs(accountsStore);

  const { bindings: surveyOptionsBindings, load: loadSurveyOptions } = useSurveyOptions();

  const selectedTemplate = ref(null);
  const isCustomQuery = ref(true);
  const queryName = ref("Custom Query");
  const editedSql = ref("");
  const spreadsheetId = ref("");
  const tabName = ref("");
  const templateParams = ref({});
  const isRunning = ref(false);

  const isTemplateDialogOpen = ref(false);
  const editingTemplate = ref(null);

  const instance = getCurrentInstance();
  const cable = instance?.proxy?.$cable;

  const isEditable = computed(() => isCustomQuery.value || isSuperAdmin.value);

  const sqlWasModified = computed(() => selectedTemplate.value && editedSql.value !== selectedTemplate.value.sql_query);

  const canRun = computed(() => {
    if (!queryName.value.trim()) return false;
    if (!spreadsheetId.value) return false;
    if (!tabName.value.trim()) return false;
    if (!editedSql.value.trim()) return false;
    if (!isCustomQuery.value && !selectedTemplate.value) return false;
    if (selectedTemplate.value && !sqlWasModified.value) {
      const params = selectedTemplate.value.params || [];
      for (const param of params) {
        const value = templateParams.value[param.name];
        const isEmpty = Array.isArray(value) ? value.length === 0 : !value;
        if (isEmpty) return false;
      }
    }
    return true;
  });

  function selectTemplate(template) {
    selectedTemplate.value = template;
    isCustomQuery.value = false;
    queryName.value = template.name;
    editedSql.value = template.sql_query;
    templateParams.value = {};
  }

  function selectCustomQuery() {
    selectedTemplate.value = null;
    isCustomQuery.value = true;
    queryName.value = "Custom Query";
    editedSql.value = "";
    templateParams.value = {};
  }

  function openSaveDialog() {
    editingTemplate.value = null;
    isTemplateDialogOpen.value = true;
  }

  function openEditDialog(template) {
    editingTemplate.value = template;
    isTemplateDialogOpen.value = true;
  }

  function handleTemplateSaved(template) {
    selectTemplate(template);
  }

  async function handleDeleteTemplate(template) {
    try {
      await sqlQueryStore.deleteTemplate(template.id);
      if (selectedTemplate.value?.id === template.id) selectCustomQuery();
      toast.success("Template deleted");
    } catch (error) {
      toast.error(error.message || "Failed to delete template");
    }
  }

  function loadFromRun(job) {
    const attrs = job.job_attributes || {};

    queryName.value = job.name || "Custom Query";
    spreadsheetId.value = attrs.spreadsheet_id || "";
    tabName.value = attrs.tab_name || "";

    if (job.job_type === "library_query" && attrs.template_id) {
      const template = sqlQueryStore.templates.find((t) => t.id === attrs.template_id);
      if (template) {
        selectedTemplate.value = template;
        isCustomQuery.value = false;
        editedSql.value = template.sql_query;
        templateParams.value = { ...(attrs.template_params || {}) };
        return;
      }
    }

    selectedTemplate.value = null;
    isCustomQuery.value = true;
    editedSql.value = job.query || "";
    templateParams.value = {};
  }

  async function rerunJob(job) {
    try {
      await sqlQueryStore.rerunJob(job.id);
      toast.success("Query re-run started");
    } catch (error) {
      toast.error(error.message || "Failed to re-run query");
    }
  }

  async function deleteRun(jobId) {
    await sqlQueryStore.deleteRun(jobId);
  }

  const paramOptions = computed(() => ({
    campaign_select: mCampaignsOptions.value,
    account_select: availableAccountsOptions.value,
  }));

  function handleParamSearch(paramType, query) {
    if (paramType === "campaign_select") {
      mCampaignsStore.mCampaignsSearchQuery = query;
    }
  }

  async function runQuery() {
    isRunning.value = true;

    try {
      if (isCustomQuery.value || sqlWasModified.value) {
        await sqlQueryStore.runCustomQuery({
          name: queryName.value,
          spreadsheet_id: spreadsheetId.value,
          tab_name: tabName.value,
          query: editedSql.value,
        });
      } else {
        await sqlQueryStore.runLibraryQuery({
          name: queryName.value,
          template_id: selectedTemplate.value.id,
          spreadsheet_id: spreadsheetId.value,
          tab_name: tabName.value,
          template_params: templateParams.value,
        });
      }
    } catch (error) {
      toast.error(error.message || "Query failed");
    } finally {
      isRunning.value = false;
    }
  }

  onMounted(async () => {
    await Promise.all([
      sqlQueryStore.fetchTemplates(),
      sqlQueryStore.fetchRecentRuns(),
      loadSurveyOptions(1),
      mCampaignsStore.search(),
      accountsStore.fetch(),
    ]);

    if (cable) {
      cable.subscribe({ channel: "JobsStatusChannel" });
      cable._addChannel(
        "JobsStatusChannel",
        {
          connected: () =>
            cable.perform({
              channel: "JobsStatusChannel",
              action: "subscribe",
            }),
          received: (msg) => {
            if (msg.jid && msg.status) {
              sqlQueryStore.updateJobStatus(msg.jid, msg.status);
            }
          },
        },
        { _uid: instance.uid, _value: instance.proxy },
      );
    }
  });

  onBeforeUnmount(() => {
    if (cable) {
      cable.unsubscribe("JobsStatusChannel");
    }
  });
</script>

<template>
  <ThreePanePage
    :span-left="3"
    :span-center="6"
    :span-right="3">
    <template #left>
      <TemplateList
        :templates="sqlQueryStore.templates"
        :selected-template-id="selectedTemplate?.id"
        :is-custom-query="isCustomQuery"
        :is-super-admin="isSuperAdmin"
        @select-template="selectTemplate"
        @select-custom="selectCustomQuery"
        @edit-template="openEditDialog"
        @delete-template="handleDeleteTemplate" />
    </template>

    <template #center>
      <div
        v-if="selectedTemplate || isCustomQuery"
        class="space-y-4">
        <Card class="p-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div class="sm:col-span-2 space-y-2">
              <Label>Run name</Label>
              <Input
                v-model="queryName"
                placeholder="Run name"
                class="mt-1" />
            </div>
            <div class="space-y-2">
              <Label>Google Sheets ID</Label>
              <Input
                v-model="spreadsheetId"
                placeholder="Spreadsheet ID"
                class="mt-1" />
            </div>
            <div class="space-y-2">
              <Label>Tab name</Label>
              <Input
                v-model="tabName"
                placeholder="data"
                class="mt-1" />
            </div>

            <div
              v-for="param in selectedTemplate?.params || []"
              :key="param.name"
              class="space-y-2">
              <Label>{{ param.label || param.name }}</Label>
              <Select
                v-if="['survey_select', 'survey_multiselect'].includes(param.type)"
                :model-value="param.type === 'survey_multiselect' ? templateParams[param.name] || [] : templateParams[param.name]"
                v-bind="surveyOptionsBindings"
                :multiple="param.type === 'survey_multiselect'"
                :placeholder="`Select ${param.label || param.name}`"
                class="mt-1"
                @update:model-value="templateParams[param.name] = $event" />
              <Select
                v-else-if="['campaign_select', 'account_select'].includes(param.type)"
                :model-value="templateParams[param.name]"
                :options="paramOptions[param.type] || []"
                :placeholder="`Select ${param.label || param.name}`"
                filterable
                :remote="param.type === 'campaign_select'"
                :loading="param.type === 'campaign_select' && mCampaignsLoading"
                class="mt-1"
                @update:model-value="templateParams[param.name] = $event"
                @search="handleParamSearch(param.type, $event)" />
              <Input
                v-else-if="param.type === 'number'"
                v-model="templateParams[param.name]"
                type="number"
                class="mt-1" />
              <Input
                v-else-if="param.type === 'date'"
                v-model="templateParams[param.name]"
                type="date"
                class="mt-1" />
              <Input
                v-else
                v-model="templateParams[param.name]"
                type="text"
                class="mt-1" />
            </div>
          </div>

          <div class="flex justify-end gap-2 mt-4">
            <Button
              v-if="isSuperAdmin && editedSql.trim()"
              variant="outline"
              @click="openSaveDialog">
              <Plus class="h-4 w-4 mr-1" />
              Save as template
            </Button>
            <Button
              :disabled="!canRun || isRunning"
              @click="runQuery">
              {{ isRunning ? "Running..." : "Run" }}
            </Button>
          </div>
        </Card>

        <Card class="p-4 space-y-2">
          <div class="flex items-center justify-between">
            <Label>SQL Query</Label>
            <span
              v-if="sqlWasModified"
              class="text-xs text-muted-foreground italic">
              Modified — will run as custom query
            </span>
          </div>
          <Textarea
            v-model="editedSql"
            :readonly="!isEditable"
            :class="!isEditable ? 'bg-muted' : ''"
            class="font-mono text-sm mt-1 min-h-[300px]"
            @keydown.tab="handleTabKey" />
        </Card>
      </div>

      <div
        v-else
        class="flex items-center justify-center h-full text-muted-foreground text-sm">
        Select a query from the list
      </div>
    </template>

    <template #right>
      <RecentRuns
        :runs="recentRuns"
        @load-run="loadFromRun"
        @rerun="rerunJob"
        @delete-run="deleteRun" />
    </template>
  </ThreePanePage>

  <TemplateFormDialog
    :open="isTemplateDialogOpen"
    :template="editingTemplate"
    :current-sql="editedSql"
    @update:open="isTemplateDialogOpen = $event"
    @saved="handleTemplateSaved" />
</template>
